import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Button, Icon, Modal, ModalSize, formatNumber } from '@column/column-ui-kit';

import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { Flex, Text } from '~/styles';
import { ACHTransferBatch } from '~/typings/API';
import { renderErrorDetails } from '~/util/apiError';

interface Props {
  batch: ACHTransferBatch;
  isOpen: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  onPendingChange?: (pending: boolean) => void;
}

interface ApiError {
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
}

const Body = styled.div`
  padding: 24px 0 8px;
`;

const Skipped = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  font-size: 13px;

  svg {
    --icon-size: 16px;
    color: ${({ theme }) => theme.warning.background};
  }
`;

const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Flex flexDirection="row" alignItems="baseline" justifyContent="space-between">
    <Text size={13} color="light">
      {label}
    </Text>
    <Text size={14} weight="medium" color="light">
      {value}
    </Text>
  </Flex>
);

const ErrorBlock = styled.div`
  border-radius: 6px;
  padding: 12px;
  font-size: 13px;
  background: ${({ theme }) => theme.danger.blendToBackground(50)};
  border: 1px solid ${({ theme }) => theme.danger.blendToBackground(150)};
  color: ${({ theme }) => theme.danger.blendToBackground(800)};
`;

export const ConfirmBatchModal: React.FC<Props> = ({ batch, isOpen, onClose, onConfirmed, onPendingChange }) => {
  const [idempotencyKey] = useState(() => uuid());
  const [apiError, setApiError] = useState<ApiError | undefined>();

  const validCount = batch.totalCount - batch.validationFailedCount;
  const skipped = batch.validationFailedCount;

  const mutation = useMutation({
    mutationFn: () => AchBatchRepository.confirm(batch.id, idempotencyKey),
    onSuccess: () => {
      setApiError(undefined);
      onConfirmed();
      onClose();
    },
    onError: (error: ApiError) => {
      if (error?.code === 'invalid_record_status') {
        onConfirmed();
        onClose();
        return;
      }
      setApiError(error);
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
    return () => onPendingChange?.(false);
  }, [mutation.isPending, onPendingChange]);

  const handleClose = () => {
    if (!mutation.isPending) onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={ModalSize.Small} withClose={false}>
      <Modal.Header title={`Create ${validCount} transfer${validCount === 1 ? '' : 's'} from this batch?`}>
        <Modal.Close variant="subtle" size="small" onClick={handleClose} isDisabled={mutation.isPending} />
      </Modal.Header>
      <Modal.Content align="left">
        <Body>
          <Flex flexDirection="column" gap={20}>
            <Flex flexDirection="column" gap={8}>
              <StatRow label="Validated credit" value={formatNumber(batch.validatedTotalCreditAmount, 'USD')} />
              <StatRow label="Validated debit" value={formatNumber(batch.validatedTotalDebitAmount, 'USD')} />
            </Flex>
            {skipped > 0 && (
              <Skipped>
                <Icon.Warning />
                {skipped} invalid record{skipped === 1 ? ' will' : 's will'} be skipped
              </Skipped>
            )}
            {apiError && (
              <ErrorBlock>
                <div>{apiError.message ?? 'Confirm failed.'}</div>
                {apiError.details && renderErrorDetails(apiError.details)}
              </ErrorBlock>
            )}
          </Flex>
        </Body>
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} isDisabled={mutation.isPending}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>
          Create transfers
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
